import { useEffect, useState, useMemo, useCallback } from 'react';
import type {
  CreateSpoolInput,
  SpoolWithRelations,
  SpoolStatus,
  SpoolLocationType,
  TenantPrinterWithDetails,
} from '@maker-toolchest/shared';
import { createSpoolSchema } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import { FilamentSelect } from '@/components/FilamentSelect';
import { useAddFilamentToLibrary } from '@/features/filaments';
import { useStorageLocations } from '@/features/storage';
import { usePrinters } from '@/features/printers';
import { useFormState } from '@/hooks/useFormState';
import { zodErrorsToFieldErrors } from '@/utils/formErrors';

interface SpoolFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSpoolInput) => Promise<void>;
  spool?: SpoolWithRelations | null;
  preselectedFilamentId?: string;
  loading?: boolean;
}

const defaultFormData: CreateSpoolInput = {
  tenantFilamentId: '',
  initialWeight: 1000,
  remainingWeight: 1000,
  locationType: 'NONE',
  status: 'IN_STORAGE',
};

const statusOptions = [
  { value: 'IN_STORAGE', label: 'In Storage' },
  { value: 'LOADED', label: 'Loaded' },
  { value: 'IN_USE', label: 'In Use' },
  { value: 'EMPTY', label: 'Empty' },
  { value: 'DISCARDED', label: 'Discarded' },
];

const locationTypeOptions = [
  { value: 'NONE', label: 'No Location' },
  { value: 'STORAGE', label: 'Storage Location' },
  { value: 'PRINTER', label: 'Printer' },
];

export function SpoolForm({
  open,
  onClose,
  onSubmit,
  spool,
  preselectedFilamentId,
  loading = false,
}: SpoolFormProps) {
  // Additional state not managed by useFormState
  const [purchaseDateStr, setPurchaseDateStr] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [selectedLibraryFilamentId, setSelectedLibraryFilamentId] = useState<string>('');

  const { data: storageData } = useStorageLocations();
  const { data: printersData } = usePrinters();
  const addToLibraryMutation = useAddFilamentToLibrary();

  const storageLocations = storageData || [];
  const printers = printersData || [];

  // Memoize default values to prevent infinite loops
  const defaultValuesWithPreselected = useMemo(() => ({
    ...defaultFormData,
    purchaseDate: new Date(), // Pre-fill to today for new spools
    ...(preselectedFilamentId && !spool ? { tenantFilamentId: preselectedFilamentId } : {}),
  }), [preselectedFilamentId, spool]);

  // Memoize itemToFormData function to prevent infinite loops
  const itemToFormData = useCallback((s: any): CreateSpoolInput => ({
    tenantFilamentId: s.tenantFilamentId,
    purchaseDate: s.purchaseDate ? new Date(s.purchaseDate) : undefined,
    purchasePrice: s.purchasePrice || undefined,
    purchaseUrl: s.purchaseUrl || undefined,
    initialWeight: s.initialWeight,
    remainingWeight: s.remainingWeight,
    emptySpoolWeight: s.emptySpoolWeight || undefined,
    locationType: s.locationType,
    tenantPrinterId: s.tenantPrinterId || undefined,
    storageLocationId: s.storageLocationId || undefined,
    status: s.status,
    notes: s.notes || undefined,
  }), []);

  // Use form state hook for main form data and validation errors
  const {
    formData,
    errors,
    handleChange: baseHandleChange,
    setFormData,
    setErrors,
    isEditing,
  } = useFormState<CreateSpoolInput>({
    defaultValues: defaultValuesWithPreselected,
    currentItem: spool as CreateSpoolInput | null,
    isOpen: open,
    itemToFormData,
  });

  // Reset additional state when form opens
  useEffect(() => {
    if (open) {
      if (spool) {
        const dateStr = spool.purchaseDate
          ? new Date(spool.purchaseDate).toISOString().split('T')[0]
          : '';
        setPurchaseDateStr(dateStr);
      } else {
        const today = new Date().toISOString().split('T')[0];
        setPurchaseDateStr(today);
      }
      setIsAddingToLibrary(false);
      setSelectedLibraryFilamentId('');
      setQuantity(1);
    }
  }, [open, spool]);

  // Wrap base handleChange with custom logic for location type clearing
  const handleChange = (field: keyof CreateSpoolInput, value: unknown) => {
    // Clear location-specific fields when changing location type
    if (field === 'locationType') {
      setFormData((prev: CreateSpoolInput) => {
        const updated: CreateSpoolInput = { ...prev, [field]: value as any };
        if (value !== 'PRINTER') {
          updated.tenantPrinterId = undefined;
        }
        if (value !== 'STORAGE') {
          updated.storageLocationId = undefined;
        }
        return updated;
      });
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    } else {
      baseHandleChange(field, value);
    }
  };

  const handleDateChange = (dateStr: string) => {
    setPurchaseDateStr(dateStr);
    if (dateStr) {
      handleChange('purchaseDate', new Date(dateStr));
    } else {
      handleChange('purchaseDate', undefined);
    }
  };

  const handleFilamentChange = (value: string, isFromLibrary: boolean) => {
    if (isFromLibrary) {
      // Extract the actual filament ID from 'library:{id}'
      const filamentId = value.replace('library:', '');
      setSelectedLibraryFilamentId(filamentId);
      // Don't set tenantFilamentId yet - we'll add it to library when submitting
      setFormData({ ...formData, tenantFilamentId: '' });
    } else {
      // Normal tenant filament selection
      setSelectedLibraryFilamentId('');
      setFormData({ ...formData, tenantFilamentId: value });
    }

    if (errors.tenantFilamentId) {
      setErrors({ ...errors, tenantFilamentId: '' });
    }
  };

  const validate = (): boolean => {
    // Additional custom validation rules
    const newErrors: Record<string, string> = {};

    if (quantity < 1 || quantity > 100) {
      newErrors.quantity = 'Quantity must be between 1 and 100';
    }
    if (!formData.tenantFilamentId) {
      newErrors.tenantFilamentId = 'Please select a filament';
    }
    if (formData.remainingWeight > formData.initialWeight) {
      newErrors.remainingWeight = 'Remaining weight cannot exceed initial weight';
    }
    if (formData.locationType === 'PRINTER' && !formData.tenantPrinterId) {
      newErrors.tenantPrinterId = 'Please select a printer';
    }
    if (formData.locationType === 'STORAGE' && !formData.storageLocationId) {
      newErrors.storageLocationId = 'Please select a storage location';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    // Validate with Zod schema
    const result = createSpoolSchema.safeParse(formData);

    if (!result.success) {
      const validationErrors = zodErrorsToFieldErrors(result.error);
      // Replace generic UUID error messages with user-friendly ones
      if (validationErrors.tenantFilamentId) {
        validationErrors.tenantFilamentId = 'Please select a filament';
      }
      setErrors(validationErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent submission if still adding to library
    if (isAddingToLibrary) {
      return;
    }

    // If a library filament was selected, add it to tenant library first
    if (selectedLibraryFilamentId) {
      setIsAddingToLibrary(true);
      setErrors({ ...errors, tenantFilamentId: '' });

      try {
        const result = await addToLibraryMutation.mutateAsync({
          filamentId: selectedLibraryFilamentId,
        });

        // Update formData with the new tenant filament ID
        const updatedFormData = {
          ...formData,
          tenantFilamentId: result.id,
        };

        // Validate with the updated tenant filament ID
        const validationResult = createSpoolSchema.safeParse(updatedFormData);
        if (!validationResult.success) {
          const validationErrors = zodErrorsToFieldErrors(validationResult.error);
          setErrors(validationErrors);
          setIsAddingToLibrary(false);
          return;
        }

        // Submit spools (create quantity number of spools)
        for (let i = 0; i < quantity; i++) {
          await onSubmit(updatedFormData);
        }
        setIsAddingToLibrary(false);
      } catch (error: any) {
        // Handle tier limits, conflicts, etc.
        if (error?.error?.code === 'TIER_LIMIT_EXCEEDED') {
          setErrors({
            ...errors,
            tenantFilamentId: 'You have reached your filament library limit. Upgrade to add more.',
          });
        } else if (error?.error?.code === 'CONFLICT') {
          setErrors({
            ...errors,
            tenantFilamentId: 'This filament is already in your library.',
          });
        } else {
          setErrors({
            ...errors,
            tenantFilamentId: 'Failed to add filament to your library. Please try again.',
          });
        }
        setIsAddingToLibrary(false);
      }
      return;
    }

    // Normal submission with tenant filament
    if (!validate()) return;

    try {
      // Submit spools (create quantity number of spools)
      for (let i = 0; i < quantity; i++) {
        await onSubmit(formData);
      }
    } catch {
      // Error handled by caller
    }
  };

  // Calculate percentage remaining
  const percentRemaining = formData.initialWeight > 0
    ? Math.round((formData.remainingWeight / formData.initialWeight) * 100)
    : 0;

  // Check if filament is selected (either from tenant library or system library)
  const hasFilamentSelected = !!(formData.tenantFilamentId || selectedLibraryFilamentId);

  const printerOptions = [
    { value: '', label: 'Select printer...' },
    ...printers.map((printer: TenantPrinterWithDetails) => ({ value: printer.id, label: printer.instanceName || printer.printer.name })),
  ];

  const storageLocationOptions = [
    { value: '', label: 'Select location...' },
    ...storageLocations.map((loc) => ({ value: loc.id, label: loc.name })),
  ];

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Spool' : 'Add Spool'}
    >
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="flex-1 space-y-6 overflow-y-auto p-4">
          {/* Filament Selection */}
          <div className="space-y-4">
            <FilamentSelect
              value={selectedLibraryFilamentId ? `library:${selectedLibraryFilamentId}` : formData.tenantFilamentId}
              onChange={handleFilamentChange}
              error={errors.tenantFilamentId}
              disabled={isAddingToLibrary || loading}
            />
            {isAddingToLibrary && (
              <p className="mt-2 text-xs text-foreground-muted">
                Adding filament to your library...
              </p>
            )}

            {/* Quantity */}
            <div>
              <Input
                label="Quantity"
                type="number"
                min={1}
                max={100}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                disabled={loading || isAddingToLibrary}
                error={errors.quantity}
              />
              {quantity > 1 && (
                <p className="mt-1.5 text-xs text-foreground-muted">
                  The settings below will apply to all {quantity} spools being created.
                </p>
              )}
            </div>
          </div>

          {/* Purchase Info */}
          <FormSection
            title="Purchase Info"
            collapsible
            defaultOpen={false}
            collapsedDescription="Purchase date will be set to today"
          >
            <div className="space-y-4">
              <Input
                label="Purchase Date"
                type="date"
                value={purchaseDateStr}
                onChange={(e) => handleDateChange(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Purchase Price"
                  type="number"
                  step="0.01"
                  min={0}
                  placeholder="e.g., 24.99"
                  value={formData.purchasePrice || ''}
                  onChange={(e) =>
                    handleChange('purchasePrice', e.target.value ? parseFloat(e.target.value) : undefined)
                  }
                />
              </div>
              <Input
                label="Purchase URL"
                type="url"
                placeholder="https://..."
                value={formData.purchaseUrl || ''}
                onChange={(e) => handleChange('purchaseUrl', e.target.value || undefined)}
              />
            </div>
          </FormSection>

          {/* Weight Tracking */}
          <FormSection
            title="Weight"
            collapsible
            defaultOpen={false}
            collapsedDescription="Default: 1kg (1000g) full spool"
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Initial Weight (g)"
                  type="number"
                  min={0}
                  value={formData.initialWeight}
                  onChange={(e) => handleChange('initialWeight', parseFloat(e.target.value) || 0)}
                  error={errors.initialWeight}
                  required
                />
                <Input
                  label="Remaining Weight (g)"
                  type="number"
                  min={0}
                  value={formData.remainingWeight}
                  onChange={(e) => handleChange('remainingWeight', parseFloat(e.target.value) || 0)}
                  error={errors.remainingWeight}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${percentRemaining}%` }}
                  />
                </div>
                <span className="text-sm text-foreground-muted">{percentRemaining}%</span>
              </div>
              <Input
                label="Empty Spool Weight (g)"
                type="number"
                min={0}
                placeholder="Optional - for accurate tracking"
                value={formData.emptySpoolWeight || ''}
                onChange={(e) =>
                  handleChange('emptySpoolWeight', e.target.value ? parseFloat(e.target.value) : undefined)
                }
              />
            </div>
          </FormSection>

          {/* Location */}
          <FormSection title="Location" collapsible defaultOpen={false}>
            <div className="space-y-4">
              <Select
                label="Location Type"
                value={formData.locationType}
                onChange={(e) => handleChange('locationType', e.target.value as SpoolLocationType)}
                options={locationTypeOptions}
              />

              {formData.locationType === 'PRINTER' && (
                <Select
                  label="Printer"
                  value={formData.tenantPrinterId || ''}
                  onChange={(e) => handleChange('tenantPrinterId', e.target.value || undefined)}
                  error={errors.tenantPrinterId}
                  options={printerOptions}
                />
              )}

              {formData.locationType === 'STORAGE' && (
                <Select
                  label="Storage Location"
                  value={formData.storageLocationId || ''}
                  onChange={(e) => handleChange('storageLocationId', e.target.value || undefined)}
                  error={errors.storageLocationId}
                  options={storageLocationOptions}
                />
              )}

              <Select
                label="Status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value as SpoolStatus)}
                options={statusOptions}
              />
            </div>
          </FormSection>

          {/* Notes */}
          <FormSection title="Notes" collapsible defaultOpen={false}>
            <Textarea
              placeholder="Any notes about this spool..."
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value || undefined)}
              rows={3}
            />
          </FormSection>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-background-subtle px-4 py-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading || isAddingToLibrary}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading || isAddingToLibrary}
            disabled={!hasFilamentSelected || loading || isAddingToLibrary}
          >
            {isEditing ? 'Save Changes' : 'Add Spool'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
